/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: garry彭
 * @LastEditTime: 2024-04-23 17:39:57
 */
import { Table, TablePaginationConfig } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import Apply from './Apply'
import { useAppDispatch, useUserId } from '../../../app/hooks'
import { fetchUndoList } from '../../../actions/invoice'

// 未开票记录类型
interface UndoRecord {
  id: number
  index: number
  productDesc: string
  groupID: string | null
  amount: number
  orderNo: string
  purchaseTime: string
}

// 分页状态类型
interface PaginationState {
  current: number
  defaultPageSize: number
  total?: number
}

// 组件暴露的方法
export interface UndoRef {
  refresh: () => void
}

const Undo = forwardRef<UndoRef>((_, ref) => {
  const dispatch = useAppDispatch()
  const [data, setData] = useState<UndoRecord[]>([])
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    defaultPageSize: 10
  })
  const userId = useUserId()
  const [applyProps, setApplyProps] = useState<{
    visible: boolean
    orderNoList: string[]
    amount: number
  }>({
    visible: false,
    orderNoList: [],
    amount: 0
  })

  const fetchData = (page: number) => {
    dispatch(
      fetchUndoList({
        userID: userId,
        page,
        pageSize: pagination.defaultPageSize
      })
    ).then((res) => {
      setData(
        res.list.map((item: Omit<UndoRecord, 'index'>, index: number) => ({
          index: (page - 1) * pagination.defaultPageSize + index + 1,
          ...item
        }))
      )

      setPagination({
        ...pagination,
        current: page,
        total: res.totalCount
      })
    })
  }

  useImperativeHandle(
    ref,
    () => {
      return {
        refresh() {
          fetchData(pagination.current)
        }
      }
    },
    [pagination.current]
  )

  useEffect(() => {
    fetchData(1)
  }, [])

  const columns: ColumnsType<UndoRecord> = [
    { title: '序号', dataIndex: 'index', width: 60 },
    { title: '商品类型', dataIndex: 'productDesc', width: 200 },
    {
      title: '团队号',
      dataIndex: 'groupID',
      width: 180,
      render: (groupID: string | null) => groupID || '-'
    },
    { title: '购买金额', dataIndex: 'amount', width: 180 },
    { title: '订单号', dataIndex: 'orderNo', width: 240 },
    { title: '购买时间', dataIndex: 'purchaseTime', width: 240 },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <>
          <a
            onClick={() =>
              setApplyProps({
                visible: true,
                orderNoList: [record.orderNo],
                amount: record.amount
              })
            }
          >
            开票
          </a>
        </>
      )
    }
  ]

  return (
    <>
      <Table<UndoRecord>
        dataSource={data}
        columns={columns}
        rowKey="id"
        pagination={pagination as TablePaginationConfig}
        onChange={({ current }) => {
          fetchData(current!)
        }}
        scroll={{
          x: columns.reduce((sum, cur) => sum + (cur.width as number || 0), 0)
        }}
      />
      <Apply
        {...applyProps}
        onClose={() =>
          setApplyProps({
            visible: false,
            orderNoList: [],
            amount: 0
          })
        }
        onConfirm={() => fetchData(pagination.current)}
      />
    </>
  )
})

export default Undo
